/**
 * components/fiscal/EfrisIdentityEditor.js
 * ----------------------------------------------------------------------------
 * The ONE editor for a vendor's EFRIS identity (WIRE_CONTRACT 17.3): which
 * taxpayer Lusuku files as, which URA device, which environment, and where the
 * vendor has got to with URA's enrolment.
 *
 * WHY IT IS A COMPONENT. This dialog lived inline in the wallet dashboard,
 * next to the commission and VAT editors, because that was where the contract
 * rows were. The Tax menu now has a Vendor tax settings page whose whole job is
 * this identity, and URA will watch that page during the UAT. Two copies of a
 * form that decides whether a sale is filed to URA for real is one copy too
 * many: a validation rule fixed on one would stay broken on the other. So the
 * wallet dashboard keeps its read-only EFRIS column and sends editing here.
 *
 * WHAT IT PROTECTS. Both checks below mirror validateEfrisSettingsInput on
 * the server so the operator is told before the round trip rather than after
 * it. Neither is auto-corrected, for the same reason the server refuses: each
 * is a step somebody has to have actually completed with URA, and quietly
 * downgrading the request would hide that it is missing.
 *
 * The environment control carries the weight. An FDN is issued at upload
 * time, cannot be backdated, and a correction goes through a credit note URA
 * approves by hand. So LIVE is spelled out in Lusuku yellow and TEST is the resting
 * state.
 *
 * Whether Lusuku fiscalises for a vendor AT ALL is a separate fact and is not
 * settable here: that is the EFRIS_FISCALISATION entitlement, so turning it on
 * is a grant with a name and a timestamp on it. VAT registration is a separate
 * dialog for the same reason: it answers "does this vendor charge 18 percent",
 * not "which taxpayer do we file as", and a restaurant under the VAT threshold
 * has no VAT number while still owing URA an e-receipt.
 *
 *   <EfrisIdentityEditor
 *     contract={row}            a GET /api/vendor-commissions row, or null to close
 *     onClose={() => ...}
 *     onSaved={() => ...}       after a successful PATCH; the caller reloads its rows
 *     onReload={() => ...}      optional; called when the server gave no reply at
 *                               all, so the caller can refresh before a retry
 *   />
 */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiUpdateVendorEfrisSettings } from "../../api/index.js";
import { AppButton, AppChoice, AppDialog, AppSelect } from "../ui";
import FiscalModeBadge from "./FiscalModeBadge.js";
import { TYPE } from "./fiscalUi.js";
import TaxHelp from "./taxHelp.js";

export const EFRIS_ENROLMENT_OPTIONS = Object.freeze([
  { value: "NOT_ENROLLED", label: "Not enrolled" },
  { value: "PENDING", label: "Pending: applied, awaiting credentials" },
  { value: "ACTIVE", label: "Active: credentials issued" },
  { value: "SUSPENDED", label: "Suspended" },
]);

const fmtVendorType = (type) => (type ? type.charAt(0) + type.slice(1).toLowerCase() : "");

const S = {
  vendor: { ...TYPE.body, fontWeight: 700, color: "#0f172a", marginBottom: 2 },
  vendorMeta: { ...TYPE.caption, color: "#64748b", marginBottom: 14 },
};

/* The environment choice. LIVE is spelled out in Lusuku yellow when chosen,
   because choosing it files for real; TEST is the resting state in the action
   navy. Drawn by the shared AppChoice so the unchosen segment is a filled
   tint, never an outline. */
const MODE_OPTIONS = [
  { value: "TEST", label: "Test (sandbox)", extra: <FiscalModeBadge mode="TEST" /> },
  { value: "LIVE", label: "Live (URA)", activeBackground: "#F2C811", activeColor: "#0F172A", extra: <FiscalModeBadge mode="LIVE" /> },
];

const EfrisIdentityEditor = ({ contract, onClose, onSaved, onReload }) => {
  const [tin, setTin] = useState("");
  const [deviceNumber, setDeviceNumber] = useState("");
  const [enrolment, setEnrolment] = useState("NOT_ENROLLED");
  const [mode, setMode] = useState("TEST");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Seed the form from the row each time a contract is opened. The row is the
  // resolved identity (TIN possibly inherited from the VAT number), so what
  // the operator sees is what the server would file under today.
  useEffect(() => {
    if (!contract) return;
    const efris = contract.efris || {};
    setTin(efris.tin || "");
    setDeviceNumber(efris.deviceNumber || "");
    setEnrolment(efris.enrolmentStatus || "NOT_ENROLLED");
    setMode(efris.mode || "TEST");
    setNote(efris.note || "");
  }, [contract]);

  const close = () => {
    if (!saving && onClose) onClose();
  };

  const save = async () => {
    if (!contract) return;
    const trimmedTin = tin.trim();
    if (enrolment === "ACTIVE" && !trimmedTin) {
      return toast.error("A vendor cannot be enrolled ACTIVE without a TIN");
    }
    if (mode === "LIVE" && enrolment !== "ACTIVE") {
      return toast.error("Only a vendor enrolled ACTIVE with URA can file LIVE");
    }
    setSaving(true);
    try {
      const res = await apiUpdateVendorEfrisSettings(contract.vendorType, contract.contractId, {
        tin: trimmedTin,
        deviceNumber: deviceNumber.trim(),
        enrolmentStatus: enrolment,
        mode,
        note: note.trim(),
      });
      toast.success(res?.data?.message || "EFRIS settings updated");
      if (onSaved) await onSaved(res?.data);
      if (onClose) onClose();
    } catch (error) {
      if (error?.response) {
        toast.error(error.response.data?.message || "EFRIS update failed");
      } else {
        // No reply at all. The row may or may not have changed, so the caller
        // is asked to reload before the operator saves again.
        toast.error("No reply from the server. The table has been reloaded: check it before saving again.");
        if (onReload) onReload();
      }
    } finally {
      setSaving(false);
    }
  };

  const inheritedTin = contract?.efris?.tinSource === "VAT";

  return (
    <AppDialog
      open={Boolean(contract)}
      onClose={close}
      busy={saving}
      size="sm"
      title="EFRIS identity"
      subtitle="Which taxpayer Lusuku files as, and where"
      footer={
        <>
          <AppButton variant="quiet" onClick={close} disabled={saving}>Cancel</AppButton>
          <AppButton onClick={save} loading={saving}>Save EFRIS settings</AppButton>
        </>
      }
    >
      {contract ? (
        <>
          <div style={S.vendor}>{contract.name}</div>
          <div style={S.vendorMeta}>
            {[fmtVendorType(contract.vendorType), contract.country].filter(Boolean).join(", ")}
          </div>

          <div className="lk-field">
            <label className="lk-field__label" htmlFor="efris-tin">Taxpayer Identification Number (TIN)<TaxHelp topic="tin" /></label>
            <input
              id="efris-tin"
              className="lk-input"
              type="text"
              maxLength={32}
              value={tin}
              onChange={(e) => setTin(e.target.value)}
              placeholder="e.g. 1000123456"
            />
            <span className="lk-field__hint">
              {inheritedTin
                ? "Currently inherited from this vendor's VAT registration. Saving stores it here as the taxpayer we file as."
                : "The taxpayer every document for this vendor is filed under. Not the same field as the VAT number, though it is usually the same digits."}
            </span>
          </div>

          <div className="lk-field">
            <label className="lk-field__label" htmlFor="efris-device">URA device number (DSN)<TaxHelp topic="deviceNumber" /></label>
            <input
              id="efris-device"
              className="lk-input"
              type="text"
              maxLength={64}
              value={deviceNumber}
              onChange={(e) => setDeviceNumber(e.target.value)}
              placeholder="Issued by URA with the credentials"
            />
            <span className="lk-field__hint">
              The non-secret half of the credentials. The signing certificate itself is never stored on this server.
            </span>
          </div>

          <div className="lk-field">
            <label className="lk-field__label" htmlFor="efris-enrolment">Enrolment with URA<TaxHelp topic="efrisEnrolment" /></label>
            <AppSelect
              id="efris-enrolment"
              block
              value={enrolment}
              onChange={(e) => setEnrolment(e.target.value)}
            >
              {EFRIS_ENROLMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </AppSelect>
          </div>

          <div className="lk-field">
            <span className="lk-field__label">Environment<TaxHelp topic="vendorEnvironment" /></span>
            <AppChoice ariaLabel="Environment" value={mode} onChange={setMode} options={MODE_OPTIONS} />
            {mode === "LIVE" ? (
              <div className="lk-note lk-note--warning" style={{ marginTop: 10 }}>
                <span>
                  <strong>Documents will be filed to URA for real.</strong> Every sale for this vendor is
                  submitted under the TIN above and gets a fiscal number the moment URA accepts it. Fiscal
                  numbers cannot be backdated or withdrawn. A mistake is corrected by a credit note that URA
                  approves by hand.
                </span>
              </div>
            ) : (
              <div className="lk-note lk-note--info" style={{ marginTop: 10 }}>
                <span>
                  <strong>Nothing reaches URA production.</strong> Submissions go to the sandbox, so a wrong TIN
                  costs a test row rather than a real document against a real taxpayer.
                </span>
              </div>
            )}
          </div>

          <div className="lk-field" style={{ marginBottom: 0 }}>
            <label className="lk-field__label" htmlFor="efris-note">Ops note</label>
            <textarea
              id="efris-note"
              className="lk-input"
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Who at URA, application reference, blockers"
            />
          </div>
        </>
      ) : null}
    </AppDialog>
  );
};

export default EfrisIdentityEditor;
